/**
 * ProcItemGrid — Shared item entry grid for all procurement transaction screens
 * (Purchase Invoice, GRN, GRC)
 *
 * Shows ALL columns from HTML prototype by default:
 *   Sl.No | ItemCode | Image | Item Name | Article Code | OEM Code | UOM |
 *   Qty | Rate | Price | MRP | WSP Rate | Disc | P/N | CGST% | SGST% | Amount |
 *   Division | Dept | Category | Sub Catg | Brand | Style | Size | Color |
 *   Design | SM Token | Commission % | Scheme | Batch No | Mfg Date | Expiry Date | Remarks
 *
 * Features:
 *   - ⚙ Columns panel to toggle visibility
 *   - 📋 row detail button (triggers onRowSelect)
 *   - ⋮ row menu (Delete, Duplicate)
 *   - 📷 image placeholder per row
 *   - GST colour coding (incl/excl/discount)
 *   - Footer: Line Count, Total Qty, Total Amount, Total Disc, Total GST, Round Off
 */
import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { loadImages } from '../../lib/imageStorage';
import ImageManagerModal from './ImageManagerModal';
import './proc-item-grid.css';

// ─── All columns definition ─────────────────────────────────────────────────
const ALL_COLS = [
  { id:'code',      label:'ItemCode',     w:90,  num:false, ro:false, core:true },
  { id:'image',     label:'Image',        w:48,  num:false, ro:true,  core:true },
  { id:'name',      label:'Item Name',    w:160, num:false, ro:true,  core:true },
  { id:'artCode',   label:'Article Code', w:80,  num:false, ro:false, core:true },
  { id:'oemCode',   label:'OEM Code',     w:68,  num:false, ro:false, core:true },
  { id:'uom',       label:'UOM',          w:50,  num:false, ro:false, core:true },
  { id:'qty',       label:'Qty',          w:56,  num:true,  ro:false, core:true },
  { id:'rate',      label:'Rate',         w:80,  num:true,  ro:false, core:true },
  { id:'price',     label:'Price',        w:75,  num:true,  ro:true,  core:true },
  { id:'mrp',       label:'MRP',          w:68,  num:true,  ro:false, core:true },
  { id:'wsp',       label:'WSP Rate',     w:68,  num:true,  ro:false, core:true },
  { id:'disc',      label:'Disc',         w:56,  num:true,  ro:false, core:true },
  { id:'pn',        label:'P/N',          w:38,  num:false, ro:false, core:true },
  { id:'cgst',      label:'CGST%',        w:52,  num:true,  ro:false, core:true },
  { id:'sgst',      label:'SGST%',        w:52,  num:true,  ro:false, core:true },
  { id:'amount',    label:'Amount',       w:110, num:true,  ro:true,  core:true },
  // Classification (visible by default from HTML prototype)
  { id:'division',  label:'Division',     w:80,  num:false, ro:false, core:false, defaultOn:true },
  { id:'dept',      label:'Dept',         w:68,  num:false, ro:false, core:false, defaultOn:true },
  { id:'category',  label:'Category',     w:80,  num:false, ro:false, core:false, defaultOn:true },
  { id:'subcatg',   label:'Sub Catg',     w:72,  num:false, ro:false, core:false, defaultOn:true },
  { id:'brand',     label:'Brand',        w:68,  num:false, ro:false, core:false, defaultOn:true },
  { id:'style',     label:'Style',        w:68,  num:false, ro:false, core:false, defaultOn:true },
  { id:'size',      label:'Size',         w:52,  num:false, ro:false, core:false, defaultOn:true },
  { id:'color',     label:'Color',        w:60,  num:false, ro:false, core:false, defaultOn:true },
  { id:'design',    label:'Design',       w:68,  num:false, ro:false, core:false, defaultOn:true },
  { id:'smToken',   label:'SM Token',     w:72,  num:false, ro:false, core:false, defaultOn:true },
  { id:'commission',label:'Commission %', w:80,  num:true,  ro:false, core:false, defaultOn:true },
  { id:'scheme',    label:'Scheme',       w:68,  num:false, ro:false, core:false, defaultOn:true },
  { id:'batchNo',   label:'Batch No',     w:72,  num:false, ro:false, core:false, defaultOn:true },
  { id:'mfgDate',   label:'Mfg Date',     w:80,  num:false, ro:false, core:false, defaultOn:true },
  { id:'expiryDate',label:'Expiry Date',  w:80,  num:false, ro:false, core:false, defaultOn:true },
  { id:'remarks',   label:'Remarks',      w:100, num:false, ro:false, core:false, defaultOn:true },
];

// ─── Row context menu (portal-based to escape overflow:auto clipping) ─────
function RowMenu({ idx, onEdit, onVoid, onDuplicate, onManageImage, onSplitBarcode, onItemHistory, onSupplierHistory, onDelete }) {
  const [open, setOpen] = useState(false);
  const [pos,  setPos]  = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);

  function openMenu(e) {
    e.stopPropagation();
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 2, left: r.left });
    }
    setOpen(o => !o);
  }

  // Close on scroll or window resize
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => { window.removeEventListener('scroll', close, true); window.removeEventListener('resize', close); };
  }, [open]);

  const act = fn => e => { e.stopPropagation(); fn && fn(idx); setOpen(false); };

  return (
    <div className="pig-rowmenu-wrap">
      <button ref={btnRef} className="pig-rowmenu-btn" type="button"
        onClick={openMenu} data-testid={`row-menu-btn-${idx}`} title="Row actions">⋮</button>
      {open && ReactDOM.createPortal(
        <>
          {/* backdrop */}
          <div style={{ position:'fixed', inset:0, zIndex:9998 }} onClick={() => setOpen(false)}/>
          {/* dropdown */}
          <div className="pig-rowmenu-dd" data-testid="row-context-menu"
            style={{ position:'fixed', top: pos.top, left: pos.left, zIndex:9999, minWidth:210 }}>
            <button onClick={act(onEdit)}>✏️ Edit Row</button>
            <button onClick={act(onVoid)}>🚫 Void Row</button>
            <button onClick={act(onDuplicate)}>📋 Duplicate Row</button>
            <div className="pig-menu-sep"/>
            <button onClick={act(onManageImage)}>🖼 Manage Image</button>
            <button onClick={act(onSplitBarcode)}>🔢 Split Barcode / IMEI Details</button>
            <div className="pig-menu-sep"/>
            <button onClick={act(onItemHistory)}>📜 Item History</button>
            <button onClick={act(onSupplierHistory)}>🏭 Supplier History</button>
            <div className="pig-menu-sep"/>
            <button className="danger" onClick={act(onDelete)}>🗑 Delete Row</button>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────
export default function ProcItemGrid({
  rows = [],
  selIdx = null,
  onRowChange,    // (idx, field, value) => void
  onRowSelect,    // (idx) => void
  onRowDelete,    // (idx) => void
  onRowDuplicate, // (idx) => void
  onRowEdit,      // (idx) => void — Edit Row context menu (opens NewItemModal pre-filled)
  onRowVoid,      // (idx) => void — Void Row
  onAddEmptyRow,  // () => void
  // Context menu modals
  onRowItemHistory,      // (idx) => void
  onRowSupplierHistory,  // (idx) => void
  onRowSplitBarcode,     // (idx) => void
  totQty    = 0,
  totAmount = 0,
  totDisc   = 0,
  totGST    = 0,
  roundOff  = '0.00',
}) {
  // Which columns are visible — default: core + defaultOn
  const defaultVisible = new Set(ALL_COLS.filter(c => c.core || c.defaultOn).map(c => c.id));
  const [visibleCols, setVisibleCols] = useState(defaultVisible);
  const [frozenCols,  setFrozenCols]  = useState(new Set(['code']));
  const [colOrder,    setColOrder]    = useState(() => ALL_COLS.map(c => c.id));
  const [dragColId,   setDragColId]   = useState(null);
  const [dropTarget,  setDropTarget]  = useState(null);
  const [colPanOpen,  setColPanOpen]  = useState(false);
  const [imgModal,    setImgModal]    = useState(null); // { row, idx }
  const colPanRef = useRef(null);

  useEffect(() => {
    if (!colPanOpen) return;
    const h = e => { if (colPanRef.current && !colPanRef.current.contains(e.target)) setColPanOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [colPanOpen]);

  const activeCols = colOrder
    .map(id => ALL_COLS.find(c => c.id === id))
    .filter(c => c && (c.id === 'image' || visibleCols.has(c.id)));
  // Frozen cols get sticky position; compute left offsets (Sl.No=72px + preceding frozen cols)
  const frozenActive = activeCols.filter(c => frozenCols.has(c.id));
  function getFrozenLeft(colId) {
    let left = 72; // Sl.No col width
    for (const c of frozenActive) {
      if (c.id === colId) break;
      left += c.w;
    }
    return left;
  }

  function toggleCol(id) {
    setVisibleCols(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }
  function toggleFreeze(id) {
    setFrozenCols(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }

  // Column drag-to-reorder handlers
  function handleColDragStart(e, colId) {
    setDragColId(colId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', colId);
  }
  function handleColDragOver(e, colId) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTarget(colId);
  }
  function handleColDrop(e, targetColId) {
    e.preventDefault();
    if (!dragColId || dragColId === targetColId) { setDragColId(null); setDropTarget(null); return; }
    setColOrder(prev => {
      const n = [...prev];
      const fi = n.indexOf(dragColId);
      const ti = n.indexOf(targetColId);
      if (fi === -1 || ti === -1) return prev;
      n.splice(fi, 1);
      n.splice(ti, 0, dragColId);
      return n;
    });
    setDragColId(null);
    setDropTarget(null);
  }
  function handleColDragEnd() { setDragColId(null); setDropTarget(null); }

  function rowClass(row, idx) {
    if (idx === selIdx) return 'selected';
    let c = row.gstType === 'incl' ? 'pig-gst-incl' : 'pig-gst-excl';
    if (parseFloat(row.disc) > 0) c += ' pig-has-disc';
    return c;
  }

  function fmt(v, dec = 2) { return (parseFloat(v) || 0).toFixed(dec); }
  function fmtIn(v) { return (parseFloat(v) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

  function renderCell(col, row, idx) {
    const v = row[col.id] ?? '';
    if (col.id === 'image') {
      const itemKey = row.code || `row_${row._id}`;
      const imgs = loadImages(itemKey);
      const thumb = imgs[0]?.thumb || row._imgUrl || null;
      return (
        <td key="image" style={{ textAlign: 'center', width: 48, padding: 2 }}>
          <button className="pig-img-btn" type="button"
            title={thumb ? `${imgs.length} image(s) — click to manage` : 'Attach image'}
            onClick={e => { e.stopPropagation(); setImgModal({ row, idx }); }}
            style={{ position: 'relative', width: 32, height: 32, padding: 0 }}>
            {thumb
              ? <img src={thumb} alt="" style={{ width: 30, height: 30, objectFit: 'cover', borderRadius: 2, display: 'block' }} />
              : <span style={{ fontSize: 18 }}>📷</span>
            }
            {imgs.length > 0 && (
              <span style={{ position: 'absolute', top: -3, right: -3, background: 'var(--pi2-accent2)', color: '#fff', fontSize: 8, fontWeight: 700, borderRadius: '50%', width: 13, height: 13, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{imgs.length}</span>
            )}
          </button>
        </td>
      );
    }
    if (col.id === 'pn') return (
      <td key="pn" style={{ width: col.w }}>
        <span className={`pig-tag pig-tag-${(v || 'P').toLowerCase() === 'n' ? 'n' : 'p'}`}>{v || 'P'}</span>
      </td>
    );
    if (col.ro) return (
      <td key={col.id} className={col.num ? 'pig-num' : col.id === 'code' ? 'pig-code' : ''}
        style={{ minWidth: col.w, width: col.w }}>
        <span className={col.num ? 'pig-mono' : ''}>{v}</span>
      </td>
    );
    return (
      <td key={col.id} className={col.num ? 'pig-num' : col.id === 'code' ? 'pig-code' : ''}
        style={{ minWidth: col.w, width: col.w }}>
        <input
          className="pig-cell-edit"
          value={v}
          onChange={e => onRowChange && onRowChange(idx, col.id, e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === 'Tab') {
              e.preventDefault();
              if (idx === rows.length - 1) onAddEmptyRow && onAddEmptyRow();
            }
          }}
        />
      </td>
    );
  }

  return (
    <div className="pig-gridcard">
      {/* Toolbar */}
      <div className="pig-gridtoolbar">
        <span className="pig-gridtb-title"><span className="pig-dot" />Item Grid</span>
        <span className="pig-gst-legend">
          <span className="pig-lg-sw pig-lg-incl" />GST Incl.
          <span className="pig-lg-sw pig-lg-excl" style={{ marginLeft: 4 }} />GST Excl.
          <span className="pig-lg-sw pig-lg-disc" style={{ marginLeft: 4 }} />Discount Applied
        </span>
        <button className="pig-addfld" type="button" onClick={() => setColPanOpen(o => !o)}>⚙ Columns</button>

        {/* Column panel */}
        <div className={`pig-colpanel${colPanOpen ? ' open' : ''}`} ref={colPanRef}>
          <div className="pig-colpanel-head">Grid Columns (Core)</div>
          <div className="pig-colpanel-list">
            {ALL_COLS.filter(c => c.core && c.id !== 'image').map(c => (
              <div className={`pig-colrow${frozenCols.has(c.id) ? ' frozen' : ''}`} key={c.id}>
                <input type="checkbox" checked readOnly title="Core column always visible" />
                <span className="pig-colname">{c.label}</span>
                <button className={`pig-freeze-btn${frozenCols.has(c.id) ? ' active' : ''}`}
                  type="button" title={frozenCols.has(c.id) ? 'Unfreeze column' : 'Freeze column'}
                  onClick={e => { e.stopPropagation(); toggleFreeze(c.id); }}>
                  {frozenCols.has(c.id) ? '📌' : '❄'}
                </button>
              </div>
            ))}
          </div>
          <div className="pig-colpanel-head" style={{ borderTop: '1px solid var(--pi2-border-s)' }}>Available Fields</div>
          <div className="pig-colpanel-list">
            {ALL_COLS.filter(c => !c.core).map(c => (
              <div className={`pig-colrow${frozenCols.has(c.id) ? ' frozen' : ''}`} key={c.id}>
                <input type="checkbox" checked={visibleCols.has(c.id)} onChange={() => toggleCol(c.id)}
                  title={visibleCols.has(c.id) ? 'Hide column' : 'Show column'} />
                <span className="pig-colname">{c.label}</span>
                {visibleCols.has(c.id) && (
                  <button className={`pig-freeze-btn${frozenCols.has(c.id) ? ' active' : ''}`}
                    type="button" title={frozenCols.has(c.id) ? 'Unfreeze column' : 'Freeze column'}
                    onClick={e => { e.stopPropagation(); toggleFreeze(c.id); }}>
                    {frozenCols.has(c.id) ? '📌' : '❄'}
                  </button>
                )}
              </div>
            ))}
          </div>
          <div className="pig-colpanel-foot">
            <button className="pig-addfld" type="button"><span className="pig-plus">+</span>Custom Column</button>
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="pig-gridwrap">
        <table className="pig-itemgrid" data-testid="proc-item-grid">
          <thead>
            <tr>
              <th className="pig-slno-col">Sl.No</th>
              {activeCols.map(c => {
                const isFrz = frozenCols.has(c.id);
                const isDrag = dragColId === c.id;
                const isDrop = dropTarget === c.id && dragColId !== c.id;
                return (
                  <th key={c.id}
                    className={isFrz ? 'pig-th-frozen' : ''}
                    draggable={!isFrz}
                    onDragStart={e => handleColDragStart(e, c.id)}
                    onDragOver={e => handleColDragOver(e, c.id)}
                    onDrop={e => handleColDrop(e, c.id)}
                    onDragEnd={handleColDragEnd}
                    style={{
                      minWidth: c.w, width: c.w,
                      cursor: isFrz ? 'default' : 'grab',
                      opacity: isDrag ? 0.5 : 1,
                      borderLeft: isDrop ? '2px solid #60a5fa' : undefined,
                      ...(isFrz ? { position: 'sticky', left: getFrozenLeft(c.id), zIndex: 4 } : {}),
                    }}>
                    {!isFrz && <span style={{ opacity: 0.5, marginRight: 3, fontSize: 10 }}>⠿</span>}
                    {c.label}{isFrz ? ' 📌' : ''}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={row._id || ri} className={rowClass(row, ri)} onClick={() => onRowSelect && onRowSelect(ri)}>
                <td className="pig-slno-col">
                  <div className="pig-slno-wrap">
                    <span className="pig-slno">{ri + 1}</span>
                    <button className="pig-rowdetail-btn" type="button"
                      onClick={e => { e.stopPropagation(); onRowSelect && onRowSelect(ri); }}
                      title="Open row detail">📋</button>
                    <RowMenu
                      idx={ri}
                      onEdit={onRowEdit}
                      onVoid={onRowVoid || (idx => {})}
                      onDuplicate={onRowDuplicate}
                      onManageImage={idx => setImgModal({ row: rows[idx], idx })}
                      onSplitBarcode={onRowSplitBarcode || (idx => alert(`Split Barcode — row ${idx + 1}`))}
                      onItemHistory={onRowItemHistory || (idx => alert(`Item History — row ${idx + 1}`))}
                      onSupplierHistory={onRowSupplierHistory || (idx => alert(`Supplier History — row ${idx + 1}`))}
                      onDelete={onRowDelete}
                    />
                  </div>
                </td>
                {activeCols.map(col => {
                  const isFrz = frozenCols.has(col.id);
                  const cell = renderCell(col, row, ri);
                  // Clone cell with frozen styles if needed
                  if (!isFrz) return cell;
                  return React.cloneElement(cell, {
                    style: {
                      position: 'sticky',
                      left: getFrozenLeft(col.id),
                      zIndex: 2,
                      background: ri === selIdx ? 'rgba(26,86,167,.10)' : 'var(--pi2-surface, #fff)',
                      ...cell.props.style,
                    },
                  });
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="pig-gridfoot">
        {[
          ['Line Count', rows.length],
          ['Total Qty',  fmt(totQty)],
          ['Total Amount', fmtIn(totAmount)],
          ['Total Disc', fmt(totDisc)],
          ['Total GST',  fmt(totGST)],
          ['Round Off',  roundOff],
        ].map(([lbl, val]) => (
          <div className="pig-box" key={lbl}>
            <span className="pig-lbl">{lbl}</span>
            <span className="pig-mono">{val}</span>
          </div>
        ))}
      </div>

      {/* Image Manager Modal (opens from 📷 cell or Manage Image context menu) */}
      {imgModal && (
        <ImageManagerModal
          itemKey={imgModal.row.code || `row_${imgModal.row._id}`}
          itemLabel={imgModal.row.name || 'Item'}
          onSave={(ref) => {
            // Store image reference in row data
            onRowChange && onRowChange(imgModal.idx, '_imgRef', ref);
            onRowChange && onRowChange(imgModal.idx, '_imgUrl', ref.firstThumb);
          }}
          onClose={() => setImgModal(null)}
        />
      )}
    </div>
  );
}
